import type { Dayjs } from 'dayjs';
import type { CompositionEditorRow } from './compositionEditorAdapter';
import type {
  ClosingPackageCompositionResult,
  ClosingPackageProjection,
} from '../types';
import { trimOptionalComment } from './commentPolicy';
import { validateTaskModalEventDate } from './taskModalValidation';

export type FormationValidationCode =
  | 'EVENT_DATE_REQUIRED'
  | 'LOCKED'
  | 'ALREADY_FORMED'
  | 'RECOMMENDED_MODE'
  | 'COMPOSITION_EMPTY'
  | 'REQUIRED_DOCUMENT_MISSING'
  | 'REQUIRED_DOCUMENT_BLOCKED';

export type FormationValidationIssue = {
  code: FormationValidationCode;
  message: string;
  itemLabel?: string;
};

export type ValidateFormationFormInput = {
  eventAt: Dayjs | null | undefined;
  comment: string | null | undefined;
};

export type ValidateFormationCompositionInput = {
  projection: ClosingPackageProjection;
  composition: ClosingPackageCompositionResult | null | undefined;
  rows: CompositionEditorRow[];
};

export type FormationCompositionValidationMode = 'formation' | 'revision';

export function validateFormationFormFields(
  input: ValidateFormationFormInput
): FormationValidationIssue | null {
  return validateTaskModalEventDate(
    input.eventAt,
    'Укажите дату формирования комплекта'
  ) as FormationValidationIssue | null;
}

export function validateFormationComposition(
  input: ValidateFormationCompositionInput,
  mode: FormationCompositionValidationMode = 'formation'
): FormationValidationIssue | null {
  const { projection, composition, rows } = input;

  if (mode === 'revision') {
    // Revision re-formation: package is unlocked and not formed after client rejection.
  } else {
    if (projection.locked) {
      return {
        code: 'LOCKED',
        message: 'Комплект заблокирован для изменений',
      };
    }

    if (projection.formed) {
      return {
        code: 'ALREADY_FORMED',
        message: 'Комплект уже сформирован',
      };
    }
  }

  if (projection.compositionMode === 'RECOMMENDED') {
    return {
      code: 'RECOMMENDED_MODE',
      message: 'Примените рекомендуемый состав перед формированием комплекта',
    };
  }

  const includedRows = rows.filter((row) => row.compositionStatus === 'INCLUDED');
  const persistedCount = composition?.items?.length ?? 0;

  if (includedRows.length === 0 && persistedCount === 0) {
    return {
      code: 'COMPOSITION_EMPTY',
      message: 'Состав комплекта пуст. Добавьте документы или примените рекомендуемый состав',
    };
  }

  for (const row of includedRows) {
    if (!row.required || row.isManual) {
      continue;
    }

    if (row.linkedDocumentId == null || row.state === 'MISSING') {
      return {
        code: 'REQUIRED_DOCUMENT_MISSING',
        message: `Не выбран обязательный документ: ${row.documentLabel}`,
        itemLabel: row.documentLabel,
      };
    }

    const openTaskCount = row.item.openTasks?.length ?? 0;
    if (openTaskCount > 0) {
      return {
        code: 'REQUIRED_DOCUMENT_BLOCKED',
        message: `Обязательный документ «${row.documentLabel}» заблокирован открытыми задачами`,
        itemLabel: row.documentLabel,
      };
    }
  }

  return null;
}

export function validateFormationSubmit(input: {
  form: ValidateFormationFormInput;
  composition: ValidateFormationCompositionInput;
}): FormationValidationIssue | null {
  return validateFormationFormFields(input.form) ?? validateFormationComposition(input.composition);
}

export function buildFormationConfirmBody(
  comment: string | null | undefined,
  formedAt?: string | null
): { comment?: string; formedAt?: string } {
  const body: { comment?: string; formedAt?: string } = {};
  const trimmedComment = trimOptionalComment(comment);
  if (trimmedComment) {
    body.comment = trimmedComment;
  }
  if (formedAt) {
    body.formedAt = formedAt;
  }
  return body;
}
